/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Lock, Check, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { passwordResetService, supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import PasswordRequirements from '../components/common/PasswordRequirements';

const PasswordResetPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { adminStatus } = useAuth();
  /**
   * Whether to speak to this person as an invited admin rather than someone resetting a password.
   * Mechanically the two are identical — same recovery session, same rules — but the invitee has
   * never had a password, so "reset" is the wrong word, and they belong on /campaign afterwards
   * rather than at the sponsor sign-in form.
   *
   * Keyed on the admin row's own status, NOT on `isAdmin`: `isAdmin` is true for every admin,
   * invited or not, so an existing admin doing an ordinary password reset would otherwise see invite
   * wording and land on /campaign instead of the sign-in form. `pending` means the row has never
   * activated, which is exactly this person.
   *
   * Also not a query parameter: GoTrue drops any redirect it cannot match to site_url or an exact
   * allow-list entry, and a query string is exactly what stops `http://localhost:3000/reset-password`
   * matching. `?context=admin-invite` is still honoured when present, so an explicitly allow-listed
   * link keeps working.
   */
  const isAdminInvite = adminStatus === 'pending' || searchParams.get('context') === 'admin-invite';
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [sessionValid, setSessionValid] = useState<boolean | null>(null);
  const [passwordStrength, setPasswordStrength] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecialChar: false
  });

  // Check if user has valid reset session
  useEffect(() => {
    const verifySession = async () => {
      try {
        const { isValid } = await passwordResetService.verifyResetSession();
        setSessionValid(isValid);
        
        if (!isValid) {
          setError('Invalid or expired reset link. Please request a new password reset.');
        }
      } catch (error) {
        console.error('Error verifying session:', error);
        setSessionValid(false);
        setError('Invalid or expired reset link. Please request a new password reset.');
      }
    };

    verifySession();
  }, []);

  // Password strength checker
  useEffect(() => {
    const password = formData.password;
    setPasswordStrength({
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    });
  }, [formData.password]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const isPasswordStrong = () => {
    return Object.values(passwordStrength).every(Boolean);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Validation
      if (!formData.password || !formData.confirmPassword) {
        throw new Error('Please fill in all fields');
      }

      if (formData.password !== formData.confirmPassword) {
        throw new Error('Passwords do not match');
      }

      if (formData.password.length < 8) {
        throw new Error('Password must be at least 8 characters long');
      }

      if (!isPasswordStrong()) {
        throw new Error('Password must meet all security requirements');
      }

      await passwordResetService.updatePassword(formData.password);

      if (isAdminInvite) {
        // Flips the row from pending to active now, the moment a password actually gets set —
        // not on next sign-in (AuthContext used to do that fire-and-forget, which activated an
        // invite the instant the link was opened, before the form below was ever submitted).
        const { error: activateError } = await supabase.rpc('activate_admin_self');
        if (activateError) console.error('PasswordResetPage activate_admin_self:', activateError);
      }

      setSubmitted(true);

      // Redirect after success: invited admins go straight to the area they were invited for.
      setTimeout(() => {
        navigate(isAdminInvite ? '/campaign' : '/startup-signup');
      }, 3000);
    } catch (error: any) {
      console.error('Error resetting password:', error);
      
      if (error?.message?.includes('Password should be at least')) {
        setError('Password must be at least 8 characters long');
      } else if (error?.message?.includes('Same password')) {
        setError('New password must be different from your current password');
      } else if (error?.message?.includes('session')) {
        setError('Your reset session has expired. Please request a new password reset');
      } else {
        setError(error.message || 'There was an error resetting your password. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show error state if session is invalid
  if (sessionValid === false) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-light-gray to-sky-blue/20 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center border border-white/50"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"
          >
            <AlertCircle className="w-8 h-8 text-red-600" />
          </motion.div>
          
          <h2 className="text-2xl font-bold text-charcoal mb-4">
            {isAdminInvite ? 'Invite Link Expired' : 'Reset Link Expired'}
          </h2>
          <p className="text-slate-gray mb-6">
            {isAdminInvite
              ? 'This admin invite link is invalid or has expired. Ask the person who invited you to resend it.'
              : 'This password reset link is invalid or has expired. Please request a new password reset.'}
          </p>
          
          <Link to={isAdminInvite ? '/startup-signup' : '/password-reset-request'}>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="w-full px-6 py-3 bg-gradient-primary text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300"
            >
              {isAdminInvite ? 'Go to Sign In' : 'Request New Reset Link'}
            </motion.button>
          </Link>
        </motion.div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-light-gray to-sky-blue/20 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center border border-white/50"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-16 h-16 bg-gradient-to-r from-lime-green to-soft-teal rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"
          >
            <Check className="w-8 h-8 text-white" />
          </motion.div>
          
          <h2 className="text-2xl font-bold text-charcoal mb-4">
            {isAdminInvite ? 'Your Password Is Set!' : 'Password Reset Successful!'}
          </h2>
          <p className="text-slate-gray mb-6">
            {isAdminInvite
              ? 'Your admin account is ready. From now on you can sign in normally with this password.'
              : 'Your password has been successfully updated. You can now sign in with your new password.'}
          </p>
          <p className="text-sm text-electric-blue font-medium mb-6">
            {isAdminInvite ? 'Taking you to the app...' : 'Redirecting you to sign in...'}
          </p>
          
          <Link to="/startup-signup">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="w-full px-6 py-3 bg-gradient-primary text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300"
            >
              Sign In Now
            </motion.button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-light-gray to-sky-blue/20">
      {/* Header */}
      <div className="bg-gradient-to-br from-lime-green/20 to-soft-teal/15 backdrop-blur-sm border-b border-lime-green/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <Link to="/startup-signup" className="inline-flex items-center text-electric-blue hover:text-deep-indigo transition-colors mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Sign In
          </Link>
          <div className="flex items-center flex-wrap">
            <div className="w-10 h-10 bg-gradient-to-r from-lime-green to-soft-teal rounded-xl flex items-center justify-center mr-3 sm:mr-4 shadow-lg">
              <Lock className="w-6 h-6 text-white" aria-hidden="true" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-charcoal">
                {isAdminInvite ? 'Set Your Password' : 'Set New Password'}
              </h1>
              <p className="text-sm sm:text-base text-slate-gray">
                {isAdminInvite
                  ? 'Choose a password to finish setting up your admin account'
                  : 'Choose a strong password for your account'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-md mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          onSubmit={handleSubmit}
          className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden border border-white/50"
        >
          <div className="p-6 sm:p-8">
            {/* New Password */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="mb-4"
            >
              <label htmlFor="password" className="block text-sm sm:text-base font-medium text-charcoal mb-2">
                <Lock className="inline w-4 h-4 mr-1" />
                New Password *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 sm:py-4 pr-12 border border-slate-gray/30 rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 bg-white/80 text-base"
                  placeholder="Enter your new password"
                  id="password"
                  disabled={isSubmitting}
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-gray hover:text-charcoal transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </motion.div>

            {/* Password Strength Indicator */}
            {formData.password && <PasswordRequirements strength={passwordStrength} className="mb-4" />}

            {/* Confirm Password */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="mb-6"
            >
              <label htmlFor="confirmPassword" className="block text-sm sm:text-base font-medium text-charcoal mb-2">
                <Lock className="inline w-4 h-4 mr-1" />
                Confirm New Password *
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  required
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 sm:py-4 pr-12 border border-slate-gray/30 rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 bg-white/80 text-base"
                  placeholder="Confirm your new password"
                  id="confirmPassword"
                  disabled={isSubmitting}
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-gray hover:text-charcoal transition-colors"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <p className="text-red-600 text-sm mt-1">Passwords do not match</p>
              )}
            </motion.div>

            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl"
                aria-live="polite"
                role="alert"
              >
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5" />
                  <p className="text-red-600 text-sm font-medium">{error}</p>
                </div>
              </motion.div>
            )}

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={isSubmitting || !isPasswordStrong() || formData.password !== formData.confirmPassword}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full px-6 sm:px-8 py-4 sm:py-5 bg-gradient-success text-white font-semibold rounded-xl hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 text-base sm:text-lg min-h-[48px]"
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Updating Password...
                </div>
              ) : (
                <>
                  <Lock className="w-5 h-5 mr-2 inline" aria-hidden="true" />
                  Update Password
                </>
              )}
            </motion.button>
          </div>
        </motion.form>
      </div>
    </div>
  );
};

export default PasswordResetPage; 